import React, { useState } from 'react';
import { auth, db } from '../../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from "@firebase/auth";
import { collection, addDoc } from '@firebase/firestore';
import './login_signup.css'; // <-- Import the CSS file

function AdminLoginSignup() {
  const [loading, setLoading] = useState(false);
  const [showLoginPage, setShowLoginPage] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.email) return 'Please enter your email';
    if (!formData.password) return 'Please enter your password';
    if (!showLoginPage && formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    if (!showLoginPage && (!formData.firstName || !formData.lastName)) {
      return 'Please enter your first and last name';
    }
    return '';
  };

  const logUserIn = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
    } catch (err) {
      setError('Invalid email or password');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const createNewUser = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const userInfo = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const userObject = {
        userId: userInfo.user.uid,
        firstName: formData.firstName,
        lastName: formData.lastName,
        admin: true,
        verified: false,
        email: formData.email,
        createdAt: new Date().toISOString()
      };
      await addDoc(collection(db, "users"), userObject);
      setShowLoginPage(true);
    } catch (err) {
      setError('Error creating new user');
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      showLoginPage ? logUserIn(e) : createNewUser(e);
    }
  };

  return (
    <div className="admin-login-bg">
      <div className="admin-login-card">
        <h1 className="admin-login-title">
          {showLoginPage ? 'Admin Login' : 'Admin Signup'}
        </h1>

        {error && (
          <div className="admin-login-error" role="alert">
            <svg className="admin-login-error-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="admin-login-loading">
            <div className="admin-login-spinner"></div>
          </div>
        ) : (
          <form onSubmit={showLoginPage ? logUserIn : createNewUser} onKeyPress={handleKeyPress} className="admin-login-form">
            {!showLoginPage && (
              <div className="admin-login-row">
                <div className="admin-login-col">
                  <label htmlFor="firstName" className="admin-login-label">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="admin-login-input"
                    required={!showLoginPage}
                    aria-required={!showLoginPage}
                  />
                </div>
                <div className="admin-login-col">
                  <label htmlFor="lastName" className="admin-login-label">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="admin-login-input"
                    required={!showLoginPage}
                    aria-required={!showLoginPage}
                  />
                </div>
              </div>
            )}
            <div>
              <label htmlFor="email" className="admin-login-label">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="admin-login-input"
                required
                aria-required="true"
                autoComplete="username"
              />
            </div>
            <div className="admin-login-password-row">
              <label htmlFor="password" className="admin-login-label">Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="admin-login-input admin-login-password-input"
                required
                aria-required="true"
                autoComplete={showLoginPage ? "current-password" : "new-password"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="admin-login-password-toggle"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  // Eye-off icon
                  <svg className="admin-login-eye-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7s4-7 9-7c1.657 0 3.216.41 4.563 1.125M19.07 19.07l-14.14-14.14M9.88 9.88a3 3 0 104.24 4.24" />
                  </svg>
                ) : (
                  // Eye icon
                  <svg className="admin-login-eye-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {!showLoginPage && (
              <div>
                <label htmlFor="confirmPassword" className="admin-login-label">Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="admin-login-input"
                  required
                  aria-required="true"
                  autoComplete="new-password"
                />
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="admin-login-btn"
            >
              {showLoginPage ? 'Login' : 'Create Account'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowLoginPage(!showLoginPage);
                setError('');
                setFormData({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
              }}
              className="admin-login-switch"
            >
              {showLoginPage ? 'Create New Account' : 'Login with Existing Account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default AdminLoginSignup;